import React, { FC, useState } from "react";
import { useIntl } from "react-intl";
import { format, parseISO } from "date-fns";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Autocomplete from "../../Unknown/Autocomplete";
import TextField from "../../Unknown/TextField";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import InputAdornment from "../../Unknown/InputAdornment";
import CircularProgress from "../../Unknown/CircularProgress";
import useSearchPatientsForMerge, {
  PatientMergeSearchResult,
} from "./useSearchPatientsForMerge";
import messages from "./messages";

// TODO: drop once the published @Shape-Digital/kudzu-data has these fields on
// PatientMergeSearchResult (backend already returns them).
type PatientSearchOption = PatientMergeSearchResult & {
  linkedCustomerRelation?: "ward" | "family" | null;
  linkedCustomerFullName?: string | null;
};

type AddPatientToMergeSearchProps = {
  alreadySelectedIds: string[];
  onSelect: (patient: PatientMergeSearchResult) => void;
};

const AddPatientToMergeSearch: FC<AddPatientToMergeSearchProps> = ({
  alreadySelectedIds,
  onSelect,
}) => {
  const { formatMessage } = useIntl();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { results, isLoading } = useSearchPatientsForMerge(inputValue);

  return (
    <Autocomplete
      options={results as PatientSearchOption[]}
      loading={isLoading}
      open={isOpen && !isLoading}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        if (reason === "input") {
          setInputValue(value);
        }
      }}
      getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
      getOptionDisabled={(option) => alreadySelectedIds.includes(option.id)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, value) => {
        if (value) {
          onSelect(value);
          setInputValue("");
        }
      }}
      noOptionsText={
        inputValue.length < 3
          ? formatMessage(messages.searchMinChars)
          : formatMessage(messages.searchNoResults)
      }
      forcePopupIcon={false}
      componentsProps={{ paper: { elevation: 8 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={formatMessage(messages.searchLabel)}
          InputProps={{
            ...params.InputProps,
            // Design: while loading, a primary-colour spinner sits to the LEFT
            // of the search icon (the icon stays). Also visible once the popup
            // closes, so a slow search never looks unresponsive.
            endAdornment: (
              <InputAdornment position="end">
                <Box display="flex" alignItems="center" gap="8px">
                  {isLoading && <CircularProgress size={24} />}
                  <SearchOutlinedIcon color="action" />
                </Box>
              </InputAdornment>
            ),
          }}
        />
      )}
      // AC15 — result rows show last/first name, DOB, linked customer name
      // ("—" if none), and appointment count — one per line (Figma "Patient
      // Search" results), DOB as MM/dd/yyyy like the rest of the app.
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box display="flex" flexDirection="column" gap="2px">
            <Typography variant="body1">
              {option.lastName}, {option.firstName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.dateOfBirth
                ? format(parseISO(option.dateOfBirth), "MM/dd/yyyy")
                : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.linkedCustomerRelation && option.linkedCustomerFullName
                ? formatMessage(
                    option.linkedCustomerRelation === "ward"
                      ? messages.searchWardOf
                      : messages.searchFamilyMemberOf,
                    { name: option.linkedCustomerFullName },
                  )
                : option.linkedCustomerName || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMessage(messages.appointmentCount, {
                count: option.appointmentCount,
              })}
            </Typography>
          </Box>
        </li>
      )}
      filterOptions={(x) => x}
      sx={{ width: "100%" }}
    />
  );
};

export default AddPatientToMergeSearch;
